import * as z from "zod";
import { legalFeeItemConfigSchema } from "@/pages/fixedAssets/components/fixed-asset-detail/legal-fees/legalFees.schema";

export const ASSET_TYPES = [
  "land",
  "apartment",
  "house",
  "commercial_building",
  "vehicle",
  "machinery",
  "equipment",
  "other",
] as const;

export type AssetType = (typeof ASSET_TYPES)[number];

// Types that show the location field
export const LOCATION_ASSET_TYPES: AssetType[] = [
  "apartment",
  "house",
  "land",
  "commercial_building",
];

// Types that hide the location field
export const NO_LOCATION_ASSET_TYPES: AssetType[] = [
  "vehicle",
  "machinery",
  "equipment",
];

const baseSchema = z.object({
  name: z.string().min(1, "Asset name is required"),
  asset_type: z.enum(ASSET_TYPES),
  currency: z.string().min(1, "Currency is required"),
  value: z.number().min(0, "Value must be non-negative"),
  purchase_price: z.number().min(0, "Purchase price must be non-negative"),
  purchase_date: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  status: z.string().optional(),
  notes: z.string().nullable().optional(),
  ownership_document_url: z.string().nullable().optional(),
  // Property tracking fields
  tenure_type: z.enum(['freehold', 'leasehold']).nullable().optional(),
  ownership_status: z.enum(['fully_owned', 'purchasing']).optional(),
  amount_paid: z.number().min(0, "Amount paid must be non-negative").optional(),
  // Type-specific fields (all optional at base level; refined below)
  apartment_number: z.string().optional(),
  expected_rent: z.number().optional(),
  service_fee: z.number().optional(),
  rent_day_of_month: z.number().int().min(1).max(28).optional(),
  is_occupied: z.boolean().optional(),
  acres: z.number().optional(),
  land_id: z.string().uuid().nullable().optional(),
  management_fee: z.number().min(0, "Must be non-negative").optional(),
  management_fee_vat: z.number().min(0, "Must be non-negative").optional(),
  // Legal fee opt-in fields (Req 1.1–1.6)
  track_legal_fees: z.boolean().optional().default(false),
  legal_fee_items: z.array(legalFeeItemConfigSchema).optional(),
});

export const fixedAssetFormSchema = baseSchema
  .refine(
    (data) => {
      if (data.asset_type === "apartment") {
        return (
          data.apartment_number !== undefined &&
          data.apartment_number !== null &&
          data.apartment_number.trim().length > 0
        );
      }
      return true;
    },
    { message: "Apartment number is required", path: ["apartment_number"] }
  )
  .refine(
    (data) => {
      if (data.asset_type === "apartment") {
        return data.expected_rent !== undefined && data.expected_rent >= 0;
      }
      return true;
    },
    { message: "Expected rent is required", path: ["expected_rent"] }
  )
  .refine(
    (data) => {
      if (data.asset_type === "apartment") {
        return data.service_fee !== undefined && data.service_fee >= 0;
      }
      return true;
    },
    { message: "Service fee is required", path: ["service_fee"] }
  )
  .refine(
    (data) => {
      if (data.asset_type === "apartment") {
        return (
          data.rent_day_of_month !== undefined &&
          data.rent_day_of_month >= 1 &&
          data.rent_day_of_month <= 28
        );
      }
      return true;
    },
    {
      message: "Rent day of month must be between 1 and 28",
      path: ["rent_day_of_month"],
    }
  )
  .refine(
    (data) => {
      if (data.asset_type === "land") {
        return data.acres !== undefined && data.acres > 0;
      }
      return true;
    },
    { message: "Acres must be a positive number", path: ["acres"] }
  );

export type FixedAssetFormData = z.infer<typeof fixedAssetFormSchema>;
